import GIFLib from "gif.js/dist/gif";
import { GIF_SETTING_LIMITS } from "./gifConstants";
import gifWorkerSource from "./gifWorkerSource";

const GIF = GIFLib && GIFLib.default ? GIFLib.default : GIFLib;

const loadHtmlImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("One of the images failed to load."));
    image.src = src;
  });

const drawContainedFrame = (ctx, image, width, height, background) => {
  ctx.fillStyle = background || "#ffffff";
  ctx.fillRect(0, 0, width, height);

  const scale = Math.min(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const offsetX = (width - drawWidth) / 2;
  const offsetY = (height - drawHeight) / 2;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
};

const cloneCanvas = (source) => {
  const copy = document.createElement("canvas");
  copy.width = source.width;
  copy.height = source.height;
  copy.getContext("2d").drawImage(source, 0, 0);
  return copy;
};

const blendCanvases = (fromCanvas, toCanvas, amount) => {
  const canvas = document.createElement("canvas");
  canvas.width = fromCanvas.width;
  canvas.height = fromCanvas.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(fromCanvas, 0, 0);
  ctx.globalAlpha = amount;
  ctx.drawImage(toCanvas, 0, 0);
  ctx.globalAlpha = 1;
  return canvas;
};

const assertSettings = ({ width, height, frameDelay, loopCount, crossfade, crossfadeSteps }) => {
  const { width: w, height: h, frameDelay: d, loopCount: l, crossfadeSteps: s } =
    GIF_SETTING_LIMITS;

  if (!Number.isFinite(width) || width < w.min || width > w.max) {
    throw new Error(`GIF width must be between ${w.min} and ${w.max} pixels.`);
  }
  if (!Number.isFinite(height) || height < h.min || height > h.max) {
    throw new Error(`GIF height must be between ${h.min} and ${h.max} pixels.`);
  }
  if (!Number.isFinite(frameDelay) || frameDelay < d.min || frameDelay > d.max) {
    throw new Error(`Frame delay must be between ${d.min} and ${d.max} milliseconds.`);
  }
  if (!Number.isFinite(loopCount) || loopCount < l.min || loopCount > l.max) {
    throw new Error(`Number of loops must be between ${l.min} and ${l.max}.`);
  }
  if (crossfade) {
    if (!Number.isFinite(crossfadeSteps) || crossfadeSteps < s.min || crossfadeSteps > s.max) {
      throw new Error(`Crossfade steps must be between ${s.min} and ${s.max}.`);
    }
  }
};

const createGifWorkerScriptUrl = () => {
  // Inline the encoder worker. Prod webpack turns import.meta.url into
  // document.baseURI, so a relative worker path is resolved against the
  // current editor route and the SPA returns HTML instead of JS.
  const source = typeof gifWorkerSource === "string" ? gifWorkerSource : gifWorkerSource.default;
  return URL.createObjectURL(new Blob([source], { type: "application/javascript" }));
};

export const generateGifBlob = async ({
  images,
  width,
  height,
  frameDelay,
  loopCount,
  crossfade,
  crossfadeSteps,
  background,
}) => {
  assertSettings({ width, height, frameDelay, loopCount, crossfade, crossfadeSteps });

  const loadedImages = await Promise.all(
    images.map((item) => loadHtmlImage(item.url))
  );

  const frames = loadedImages.map((image) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    drawContainedFrame(canvas.getContext("2d"), image, width, height, background);
    return canvas;
  });

  const workerScript = createGifWorkerScriptUrl();

  return new Promise((resolve, reject) => {
    let settled = false;

    const releaseWorker = () => {
      URL.revokeObjectURL(workerScript);
    };

    const gif = new GIF({
      workers: 2,
      quality: 4,
      width,
      height,
      workerScript,
      repeat: loopCount === 0 ? 0 : loopCount,
    });

    const addFrame = (canvas, delay) => {
      gif.addFrame(cloneCanvas(canvas).getContext("2d"), { delay, copy: true });
    };

    frames.forEach((frame, index) => {
      addFrame(frame, frameDelay);

      if (crossfade && frames.length > 1) {
        const nextFrame = frames[(index + 1) % frames.length];
        const stepDelay = Math.max(20, Math.round(frameDelay / crossfadeSteps));
        for (let step = 1; step <= crossfadeSteps; step += 1) {
          addFrame(blendCanvases(frame, nextFrame, step / (crossfadeSteps + 1)), stepDelay);
        }
      }
    });

    gif.on("finished", (blob) => {
      if (settled) return;
      settled = true;
      releaseWorker();
      if (!blob || !blob.size) {
        reject(new Error("GIF generation produced an empty file."));
        return;
      }
      resolve(blob);
    });

    gif.on("abort", () => {
      if (settled) return;
      settled = true;
      releaseWorker();
      reject(new Error("GIF generation was cancelled."));
    });

    try {
      gif.render();
    } catch (error) {
      if (settled) return;
      settled = true;
      releaseWorker();
      reject(error);
    }
  });
};
